import { useLayoutEffect } from "react";

type Engine = { mount: (root: Element) => unknown };

// The vendored engine (public/scrollcraft/scrollcraft.js) is mounted once per
// page root, the way the home page mounts it: it stamps html.sc-ready, which
// arms the [data-sc-in] reveal styles in scroll-acts.css, and then watches
// those elements. Navigation is a document load (Navigation.tsx), so every
// page gets exactly one engine lifecycle and nothing accumulates.
//
// Layout effect, not effect: the class is what arms the reveal styles, and
// setting it before paint means content never flashes in and hides again.
//
// The engine is desktop-only by design. index.html's bootstrap returns before
// it requests the script when (max-width: 767px) or (prefers-reduced-motion:
// reduce) matches, because the pinned, horizontal and scrubbed acts are a
// wide-fine-pointer composition. The side effect nobody asked for was that a
// phone also lost the ordinary fade-and-rise reveal, which is not a pinned
// act and costs nothing: html.sc-ready was never stamped, so every
// [data-sc-in] on every route simply sat there. On a majority-mobile audience
// that is most visitors seeing a site with no choreography at all.
//
// armLite below is the phone's reveal. It is deliberately not the engine: it
// is an IntersectionObserver that toggles the same .sc-in class against the
// same scroll-acts.css rules, so there is one contract and one set of styles.
// It does not touch the hero. The hero carries no data-sc-* attributes at all
// (see the note at the top of ScenicHero.tsx) and neither hero.css nor its
// rAF driver reads html.sc-ready, so nothing here can reach it.
//
// prefers-reduced-motion keeps the current behaviour: no reveal is armed, the
// document stays as it is, and the preference is honoured rather than
// reinterpreted as a shorter animation.

// If the observer never reports, content must not stay hidden. html.sc-ready
// is only stamped once an observer is really watching, and this backstop
// reveals everything if nothing at all has been revealed by the time it
// fires. It is a failure path, not a timer the normal case waits on.
//
// It is deliberately keyed on "nothing was revealed" and not on "the observer
// never called back": an IntersectionObserver delivers an initial entry for
// every target it is given, isIntersecting false, within a frame of observe().
// A flag set from the callback therefore flips almost immediately and would
// make this backstop dead code that still reads like a promise.
const LITE_BACKSTOP_MS = 4000;

function armLite(root: Element): boolean {
  if (typeof IntersectionObserver === "undefined") return false;
  const targets = new Set(
    root.querySelectorAll<HTMLElement>("[data-sc-in], [data-sc-stagger] > *"),
  );
  if (!targets.size) return false;

  let revealed = 0;
  const reveal = (el: HTMLElement) => {
    if (el.classList.contains("sc-in")) return;
    // Children of a stagger arrive in reading order, the same order and the
    // same per-child step the engine uses.
    const parent = el.parentElement;
    const step = Number(parent?.getAttribute("data-sc-stagger") ?? 0);
    if (step > 0 && parent) {
      const index = Array.prototype.indexOf.call(parent.children, el);
      if (index > 0) el.style.transitionDelay = `${index * step}ms`;
    }
    el.classList.add("sc-in");
    revealed += 1;
  };
  const revealAllRemaining = () => {
    for (const target of targets) {
      if (target.classList.contains("sc-in")) continue;
      reveal(target);
      observer.unobserve(target);
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(entry.target as HTMLElement);
        observer.unobserve(entry.target);
      }
    },
    // threshold 0, not a ratio: an element only has to cross the edge, which
    // is what a short block does too. A little inside the bottom edge, so a
    // section announces itself as it arrives rather than after it is read.
    { rootMargin: "0px 0px -12% 0px", threshold: 0 },
  );

  const watch = (el: HTMLElement) => {
    targets.add(el);
    observer.observe(el);
  };
  for (const target of targets) observer.observe(target);

  // html.sc-ready hides every [data-sc-in] until something reveals it, so it
  // is stamped only once an observer is really watching, and from here on
  // every path out of this function has to end with content visible.
  document.documentElement.classList.add("sc-ready");

  // The set above is a snapshot. Anything React inserts later, a lazily
  // mounted section or a panel that appears after a submit, would be hidden
  // by sc-ready and never observed, so it would never come back. Watch the
  // tree for those instead of documenting a rule nobody can enforce.
  const mutations = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches("[data-sc-in]")) watch(node);
        const parent = node.parentElement;
        if (parent?.hasAttribute("data-sc-stagger")) watch(node);
        node
          .querySelectorAll<HTMLElement>("[data-sc-in], [data-sc-stagger] > *")
          .forEach(watch);
      }
    }
  });
  mutations.observe(root, { childList: true, subtree: true });

  // A display:none target generates no box and can never intersect, so it
  // would sit at opacity 0 for the life of the page. The home page has one,
  // the close panel a phone does not lay out. It has to be swept, not
  // special-cased inside the observer: at the first callback the page has not
  // finished laying out and almost every target still measures zero, so
  // reading the box there revealed 27 of 30 elements at once and cost the
  // page its animation. Wait for layout, then take out only what genuinely is
  // not rendered.
  const sweepAfterLayout = () => {
    for (const target of targets) {
      if (target.classList.contains("sc-in")) continue;
      if (target.getClientRects().length === 0) {
        reveal(target);
        observer.unobserve(target);
      }
    }
    // A document that cannot scroll can never move a target through the
    // bottom 12% the rootMargin holds back, so anything still waiting here
    // would wait forever. Short legal pages are the case. A scrollHeight of
    // zero is not a short page, it is a document that has not been laid out,
    // so it must not count: treating it as short reveals the whole page at
    // once and throws the choreography away.
    const height = document.documentElement.scrollHeight;
    if (height > 0 && height <= window.innerHeight) {
      revealAllRemaining();
    }
  };
  if (document.readyState === "complete") {
    requestAnimationFrame(sweepAfterLayout);
  } else {
    window.addEventListener(
      "load",
      () => requestAnimationFrame(sweepAfterLayout),
      { once: true },
    );
  }

  window.setTimeout(() => {
    if (revealed > 0) return;
    revealAllRemaining();
  }, LITE_BACKSTOP_MS);

  // Nothing is torn down on pagehide on purpose. pagehide also fires when the
  // document enters the back/forward cache, and a page restored from there
  // does not re-run React effects: html.sc-ready is still stamped and the
  // hook will not arm again. Disconnecting here would strand every target the
  // visitor had not yet scrolled to at opacity 0 for the rest of that page's
  // life, which is the ordinary back-button path on mobile Safari. The
  // observer is restored alive with the document and resumes on its own, and
  // it dies with the document when the document really goes.
  return true;
}

// The wide layout's backstop. There the vendored engine owns the reveal, and
// nothing checked that it really did. Once html.sc-ready is stamped every
// [data-sc-in] and every stagger child is hidden, and each of these leaves
// one hidden for good with nothing thrown and nothing logged:
//   - the engine watches the targets that existed when it mounted and no
//     others, so a section React inserts later is never seen;
//   - it only looks inside the root it was handed, while scroll-acts.css hides
//     a target wherever it sits. That was the whole of the 2026-09-28 outage:
//     the engine mounted on the consent bar's wrapper and every section of the
//     page was outside it;
//   - it reveals a stagger's children only through a parent that also carries
//     data-sc-in, and only when the stagger value is a number.
// The phone path above watches each stagger child and late nodes for itself.
// The desktop had nothing.
//
// It is the engine's own flow reveal run a second time from here: the same
// observer options, the same unit (the [data-sc-in] element, or the stagger
// parent) and the same writes, so the two cannot disagree. Where the engine is
// working both fire in the same frame and the second one changes nothing.
// Where it is not, this one is enough, at the moment the engine would have
// done it. It does not wait to see whether the engine got there first:
// waiting would leave the first section of a broken page blank for as long
// as the wait, to protect an ordering that identical writes make irrelevant
// (and the two observers do not report in the order they were created).
function armSafetyNet(): () => void {
  if (typeof IntersectionObserver === "undefined") return () => {};

  const reveal = (el: Element) => {
    el.classList.add("sc-in");
    if (!el.hasAttribute("data-sc-stagger")) return;
    // A numeric stagger gets the engine's writes. One that is not a number
    // (a bare data-sc-stagger renders as "true") the engine skips, leaving the
    // children hidden; here they come up together.
    const step = parseFloat(el.getAttribute("data-sc-stagger") ?? "");
    Array.from(el.children).forEach((kid, index) => {
      // Already shown, by the engine a moment ago or as a late arrival below.
      // Writing a delay onto it now would only slow its later transitions.
      if (kid.classList.contains("sc-in")) return;
      if (!Number.isNaN(step)) {
        (kid as HTMLElement).style.transitionDelay = `${index * step}ms`;
      }
      kid.classList.add("sc-in");
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(entry.target);
        observer.unobserve(entry.target);
      }
    },
    // scrollcraft.js's flow-reveal options, to the character.
    { rootMargin: "0px 0px -12% 0px", threshold: 0.01 },
  );

  // A stagger parent is a unit even without data-sc-in. The engine skips
  // those, and their children are hidden all the same.
  const UNITS = "[data-sc-in], [data-sc-stagger]";
  document.querySelectorAll(UNITS).forEach((el) => observer.observe(el));

  // Same reason as the phone path: the set above is a snapshot, and anything
  // React inserts later is hidden by sc-ready without ever being observed.
  const mutations = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(UNITS)) observer.observe(node);
        node.querySelectorAll(UNITS).forEach((el) => observer.observe(el));
        // A child that joins a stagger after its parent arrived has missed
        // the only reveal it was ever going to get.
        const parent = node.parentElement;
        if (
          parent?.hasAttribute("data-sc-stagger") &&
          parent.classList.contains("sc-in")
        ) {
          node.classList.add("sc-in");
        }
      }
    }
  });
  mutations.observe(document.body, { childList: true, subtree: true });

  return () => {
    observer.disconnect();
    mutations.disconnect();
  };
}

// A pinned act (pan, pin or scrub) is laid out for the engine: a sticky stage
// that clips, holding content only the engine's transform brings into view.
// The engine collects [data-sc-act] once, inside the root it was handed, and
// hands the list back. A pinned act it did not take has nothing moving it, and
// that layout then strands whatever was waiting off stage: on the home page,
// seven of the industry rail's ten items at 1440 wide.
//
// The section sheets already have a static layout for a rail with no act
// attribute. It is what phones, reduced motion and the no-JS prerender get.
// So the fallback is to take the attribute away from every pinned act nothing
// is driving. React rendered that attribute and is not told. That holds
// because pages render it from a value fixed at mount (Home's `enhanced`), so
// no later render writes it back; a page that starts toggling it will need
// to ask instead. It lives here and not in the page because it then covers
// every page at once, and because a page's own file is a content source for
// the sitemap: editing one for behaviour alone would re-date that route.
const PINNED = '[data-sc-act="pan"], [data-sc-act="pin"], [data-sc-act="scrub"]';

function releaseUndrivenActs(api?: unknown) {
  const acts = (api as { acts?: { el?: Element }[] } | null | undefined)?.acts;
  const driven = new Set(Array.isArray(acts) ? acts.map((act) => act.el) : []);
  for (const act of document.querySelectorAll(PINNED)) {
    if (!driven.has(act)) act.removeAttribute("data-sc-act");
  }
}

export function useScrollEngine() {
  useLayoutEffect(() => {
    // Not the consent bar's `rebuild cc-shell` wrapper: App.tsx mounts it first
    // in the tree, so a bare ".rebuild" found it, the engine mounted there, and
    // html.sc-ready hid the page's [data-sc-in] sections with nothing watching
    // them (chleb.ai, 2026-09-28).
    const root = document.querySelector(".rebuild:not(.cc-shell)");
    if (!root) return;

    // The engine has no teardown and is mounted once per root. The safety net
    // does have one and belongs to this effect, so it is armed on every run
    // and disarmed on cleanup. React destroys layout effects when a boundary
    // above re-suspends and runs them again when it resolves: the root is
    // already mounted by then, and a net that armed only alongside the mount
    // would be gone for the rest of the document with nothing to say so.
    let disarmNet = () => {};
    const arm = () => {
      const engine = (window as Window & { ScrollCraft?: Engine }).ScrollCraft;
      const mounted = root.getAttribute("data-engine-mounted");
      if (!engine || (mounted !== null && mounted !== "true")) return false;
      if (mounted === null) {
        root.setAttribute("data-engine-mounted", "true");
        let api: unknown;
        try {
          api = engine.mount(root);
        } catch (error) {
          // A throw from a layout effect replaces the whole route with the
          // error panel. The engine is choreography: if it fails the page
          // still has to render, anything it had already hidden is the
          // safety net's to bring back, and the error still reaches the
          // console, where e2e/routes-render.spec.ts fails on it.
          console.error("Scroll engine failed to mount", error);
        }
        releaseUndrivenActs(api);
      }
      disarmNet();
      disarmNet = armSafetyNet();
      return true;
    };
    if (arm()) return () => disarmNet();

    // No engine on this document. On a phone with motion allowed that is
    // expected and permanent, so arm the lite reveal now rather than waiting
    // for a script that was never requested.
    const small = matchMedia("(max-width: 767px)").matches;
    const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (small && !still) {
      if (!root.hasAttribute("data-engine-mounted")) {
        root.setAttribute("data-engine-mounted", "lite");
        armLite(root);
      }
      return;
    }

    // Wide layout: the engine script is deferred, so a page that renders
    // before it has run arms itself once the window has loaded. The script's
    // fetch holds the load event back, so no engine by then means it is not
    // coming (blocked, missing, or served as something that is not script).
    // With reduced motion it was never requested; no page renders a pinned
    // act then, and one that did would be stranded just the same.
    const atLoad = () => {
      if (!arm()) releaseUndrivenActs();
    };
    if (document.readyState === "complete") {
      atLoad();
      return () => disarmNet();
    }
    addEventListener("load", atLoad, { once: true });
    return () => {
      removeEventListener("load", atLoad);
      disarmNet();
    };
  }, []);
}
